import * as React from "react";
import { Form, Input, Row, Col, Select } from "antd";
import { MailOutlined } from "@ant-design/icons";

import { useNavigate, useParams } from "react-router-dom";
import {
  useUserCreate,
  useUserDetails,
  useUserRoleForSelect,
  useUserUpdate,
} from "@/entities/user";
import { isProtectedRole } from "@/entities/role";
import { useMyProfile, isSelf } from "@/entities/user";
import { useFormErrorHandler } from "@/shared/hooks/useFormErrorHandler";
import { CustomButton } from "@/shared/ui";
import { Mail, Shield } from "lucide-react";
import { useTranslation } from "react-i18next";

type UserFormProps = {
  userId?: string | null;
  onClose?: () => void;
};

export default function UserForm({ userId, onClose }: UserFormProps) {
    const { t } = useTranslation();
  const [form] = Form.useForm();
  const params = useParams();
  const navigate = useNavigate();
  const { handleApiError, handleValidationFailed } = useFormErrorHandler(form);

  const userCreate = useUserCreate();
  const userUpdate = useUserUpdate();

  const effectiveId = userId ?? params?.id;
  const { data } = useUserDetails(effectiveId);
  const { data: myProfile } = useMyProfile();
  // A member must not be able to change their own role or permissions.
  const isOwnAccount = isSelf(myProfile, data?.data);
  const isEditing = !!data;
  const canEditProfileInfo = !isEditing;

  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const { data: titlesData, isLoading: rolesLoading } = useUserRoleForSelect();
  // Built-in full-access roles are unique per store and can't be assigned to
  // other members (isProtectedRole). A new store has ONLY Admin, so this is
  // empty and the dropdown used to say just "No data" — the merchant could
  // not invite anyone and wasn't told why. Say so, and point at Roles.
  const assignableRoles = (titlesData?.data ?? []).filter((r: any) => !isProtectedRole(r));
  const noAssignableRoles = !rolesLoading && assignableRoles.length === 0 && !isOwnAccount;
  const rolesPath = params?.storeId ? `/store/${params.storeId}/roles` : null;
  const goToRoles = (create: boolean) => {
    if (!rolesPath) return;
    onClose?.();
    navigate(create ? `${rolesPath}?new=1` : rolesPath);
  };
  const noRolesHint = (
    <span className="text-xs text-secondary" data-cy="invite-no-roles-hint">
      {t("users.userForm.noRolesYet")}{" "}
      {rolesPath && (
        <button
          type="button"
          onClick={() => goToRoles(true)}
          className="font-semibold text-brand hover:underline cursor-pointer"
          data-cy="invite-create-role-link"
        >
          {t("users.userForm.createRole")}
        </button>
      )}
    </span>
  );

  React.useEffect(() => {
    const initialFormValues: any = {
      name: data?.data?.name || "",
      email: data?.data?.email || "",
      // undefined, not "": AntD Select only shows its placeholder for
      // undefined, so "" left the field looking blank and unexplained.
      permissionRoleId: data?.data?.userStores?.[0]?.permissionRoleId || undefined,
    };
    form.setFieldsValue(initialFormValues);
  }, [form, data]);

  const handleSubmit = async (values: any) => {
    if (isSubmitting) return;
    // Editing your own membership would let you escalate your own
    // permissions — the role field is disabled below, this backs it up.
    if (isOwnAccount) return;
    setIsSubmitting(true);
    if (data) {
      try {
        const payload = { permissionRoleId: values.permissionRoleId };
        await userUpdate.mutateAsync({
          id: data?.data?.id,
          payload,
        });
        if (onClose) onClose();
        else navigate(-1);
      } catch (error: any) {
        handleApiError(error);
      } finally {
        setIsSubmitting(false);
      }
    }

    if (!data) {
      try {
        await userCreate.mutateAsync({
          ...values,
        });

        form.resetFields();
        if (onClose) onClose();
        else navigate(-1);
      } catch (error: any) {
        handleApiError(error);
      } finally {
        setIsSubmitting(false);
      }
    }
  };
  return (
    <div className="flex flex-col justify-between h-full">
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        onFinishFailed={handleValidationFailed}
        scrollToFirstError={{ behavior: "smooth", block: "center" }}
      >
        {/* Section 1: Personal Information — Vendor Admin only when editing */}
        {canEditProfileInfo && (
          <div className="mb-6">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-6 h-6 bg-teal-400 rounded-full flex items-center justify-center text-white text-sm font-semibold">
                1
              </div>
              <h3 className="text-base font-semibold m-0">
                {t("users.userForm.personalInformation")}</h3>
            </div>

            <Row gutter={16}>
              <Col span={24}>
                <Form.Item
                  name="name"
                  label={<span className="text-sm"> {t("users.userForm.fullName")}</span>}
                  rules={[{ required: true, message: t("users.userForm.pleaseEnterFull") }]}
                >
                  <Input placeholder={t("users.userForm.placeholder")} size="large" data-cy="invite-name-input" />
                </Form.Item>
              </Col>
            </Row>

            <Form.Item
              name="email"
              label={<span className="text-sm"> {t("users.userForm.emailAddress")}</span>}
              rules={[
                { required: true, message: t("users.userForm.pleaseEnterEmail") },
                { type: "email", message: t("users.userForm.pleaseEnterValid") },
              ]}
            >
              <Input
                disabled={!!data}
                prefix={<MailOutlined style={{ color: "var(--color-text-tertiary)" }} />}
                placeholder={t("users.userForm.placeholder1")}
                size="large"
                data-cy="invite-email-input"
              />
            </Form.Item>
          </div>
        )}

        {/* Section 2: Role & Permissions */}
        <div className="mb-6">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-6 h-6 bg-teal-400 rounded-full flex items-center justify-center text-white text-sm font-semibold">
              {canEditProfileInfo ? "2" : "1"}
            </div>
            <h3 className="text-base font-semibold m-0">{t("users.userForm.rolePermissions")}</h3>
          </div>

          <Form.Item
            name="permissionRoleId"
            label={<span className="text-sm"> {t("users.userForm.assignRole")}</span>}
            rules={[{ required: true, message: t("users.userForm.pleaseSelectA") }]}
            extra={
              isOwnAccount ? (
                <span className="text-xs text-tertiary">
                  {t("users.userForm.cannotEditOwnRole")}
                </span>
              ) : noAssignableRoles ? (
                noRolesHint
              ) : undefined
            }
          >
            <Select
              disabled={isOwnAccount}
              placeholder={t("users.userForm.placeholder2")}
              size="large"
              suffixIcon={<Shield size={16} style={{ color: "var(--color-text-tertiary)" }} />}
              loading={rolesLoading}
              options={assignableRoles.map((role: any) => ({
                label: role.name,
                value: role.id,
              }))}
              // Inside the open dropdown too — where the merchant is looking.
              notFoundContent={
                rolesLoading ? undefined : (
                  <div className="px-2 py-3 text-center">{noRolesHint}</div>
                )
              }
              data-cy="invite-role-select"
            />
          </Form.Item>
        </div>
        {/* Footer Actions removed from inside the Form so it can sit at the bottom */}
      </Form>

      {/* Footer Actions */}
      <div className="flex flex-wrap justify-end gap-3 pt-4 border-t">
        <CustomButton
          variant="outline"
          size="large"
          data-cy="invite-cancel-btn"
          onClick={() => (onClose ? onClose() : navigate(-1))}
        >
          {t("actions.cancel")}</CustomButton>

        <CustomButton
          variant="primary"
          htmlType="submit"
          data-cy="invite-submit-btn"
          onClick={() => form.submit()}
          size="large"
          icon={<Mail className="w-4 h-4" />}
          loading={userCreate?.isPending || userUpdate?.isPending}
          style={{ backgroundColor: "var(--brand-primary)" }}
        >
          {data ? t("users.userForm.updateMember") : t("users.userForm.sendInvite")}
        </CustomButton>
      </div>
    </div>
  );
}
